import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

import type { EstadoDelTecnico } from '@/lib/supabase/tipos-bd'
import { horaCorta } from '@/shared/utilidades/formato'

import { useEstadoDelEquipo } from './useEstadoDelEquipo'
import estilos from './DondeEstaElEquipo.module.css'

/**
 * DÓNDE ESTÁ EL EQUIPO, AHORA MISMO.
 *
 * Johan pidió ver de un vistazo quién está en la obra, quién almuerza, quién
 * salió de la geocerca y de quién no se sabe nada; después, que no fuera «otra
 * tarjeta súper básica», y mandó de referencia un panel de caja: color de marca
 * con profundidad, cifras con su ícono en círculo, barras finas de proporción,
 * alertas como renglones con su acción, y un renglón de chips al pie.
 *
 * Ese es el lenguaje aquí, en el morado de MEGA:
 *   · a la izquierda, cuántos tienen trabajo en curso y, debajo, un renglón por
 *     cada cosa que pide atención, con «Abrir ›»;
 *   · a la derecha, los cinco estados como tarjetas translúcidas con su cifra y
 *     qué parte del equipo es;
 *   · al pie, «Quién»: un chip por persona con su punto de estado.
 *
 * Ni mapa ni tabla: lo que importa de quien está fuera es A CUÁNTOS METROS, y
 * eso se lee escrito; y doce filas iguales esconderían justo al que se salió.
 */

type Estado = EstadoDelTecnico['estado']

/** La llave: herramienta afuera. */
function Llave() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="13"
      height="13"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3.5 17.5a1.4 1.4 0 0 0 2 2l5.8-5.8a4 4 0 0 0 5.4-5.4l-2.5 2.5-2-2 2.5-2.5Z" />
    </svg>
  )
}

/** Los íconos de cada estado, del mismo trazo que el resto del sistema. */
function Icono({ estado }: { estado: Estado | 'equipo' }) {
  const trazos: Record<string, ReactNode> = {
    fuera: (
      <>
        <path d="M12 21s-6-5.3-6-10.5a6 6 0 0 1 12 0C18 15.7 12 21 12 21Z" />
        <path d="m10 8.5 4 4M14 8.5l-4 4" />
      </>
    ),
    sin_senal: (
      <>
        <path d="M3 3l18 18" />
        <path d="M8.5 16.5a5 5 0 0 1 7 0M5 13a10 10 0 0 1 4-2.4M19 13a10 10 0 0 0-2.3-1.6M2 9.5a15 15 0 0 1 4.5-2.8M22 9.5A15 15 0 0 0 12 5.5" />
      </>
    ),
    almuerzo: (
      <>
        <path d="M4 9h12v4a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5V9Z" />
        <path d="M16 10h1.5a2.5 2.5 0 0 1 0 5H16M8 3.5v2.5M12 3.5v2.5" />
      </>
    ),
    en_obra: (
      <>
        <path d="M4 15a8 8 0 0 1 16 0" />
        <path d="M3 15h18v2.5H3zM10 7.2V5h4v2.2" />
      </>
    ),
    por_llegar: (
      <>
        <circle cx="12" cy="12" r="8.5" />
        <path d="M12 7.5V12l3 2" />
      </>
    ),
    equipo: (
      <>
        <circle cx="9" cy="8" r="3" />
        <path d="M3.5 19a5.5 5.5 0 0 1 11 0" />
        <path d="M15.5 5.2a3 3 0 0 1 0 5.6M17.5 19a5.5 5.5 0 0 0-2.4-4.5" />
      </>
    ),
  }
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {trazos[estado]}
    </svg>
  )
}

const ESTADOS: { estado: Estado; titulo: string; tono: string; pie: string }[] = [
  { estado: 'en_obra', titulo: 'En la obra', tono: 'enObra', pie: 'dentro de su geocerca' },
  { estado: 'almuerzo', titulo: 'En almuerzo', tono: 'almuerzo', pie: 'con la orden en pausa' },
  { estado: 'por_llegar', titulo: 'Por llegar', tono: 'porLlegar', pie: 'con trabajo hoy' },
  { estado: 'fuera', titulo: 'Fuera de la obra', tono: 'fuera', pie: 'en horario, lejos' },
  { estado: 'sin_senal', titulo: 'No se sabe', tono: 'sinSenal', pie: 'sin ubicación reciente' },
]

const TONO: Record<Estado, string> = {
  fuera: 'fuera',
  sin_senal: 'sinSenal',
  almuerzo: 'almuerzo',
  en_obra: 'enObra',
  por_llegar: 'porLlegar',
  sin_trabajo: 'sinTrabajo',
}

const ORDEN_CHIP: Estado[] = [
  'fuera',
  'sin_senal',
  'almuerzo',
  'por_llegar',
  'en_obra',
  'sin_trabajo',
]

/** «1 h 10 min», «25 min». */
function lapso(desdeIso: string): string {
  const min = Math.max(0, Math.round((Date.now() - new Date(desdeIso).getTime()) / 60_000))
  if (min < 60) return `${min} min`
  const h = Math.floor(min / 60)
  const resto = min % 60
  return resto ? `${h} h ${resto} min` : `${h} h`
}

/** «30 sept»: sin año, que en un panel de hoy sobra. */
function dia(iso: string): string {
  return new Date(iso).toLocaleDateString('es-CO', {
    day: 'numeric',
    month: 'short',
    timeZone: 'America/Bogota',
  })
}

const esDeHoy = (iso: string) =>
  new Date(iso).toLocaleDateString('es-CO', { timeZone: 'America/Bogota' }) ===
  new Date().toLocaleDateString('es-CO', { timeZone: 'America/Bogota' })

/** Lo que hay que saber de cada uno, y si pide atención. */
function detalle(t: EstadoDelTecnico): { linea: string; alerta: boolean } {
  switch (t.estado) {
    case 'fuera':
      return { linea: `a ${t.distancia_m ?? '?'} m de la obra`, alerta: true }
    case 'sin_senal':
      return {
        linea:
          t.posicion_hace_min === null
            ? 'su teléfono nunca ha mandado ubicación'
            : `sin ubicación hace ${t.posicion_hace_min} min`,
        alerta: true,
      }
    case 'almuerzo':
      return {
        linea: !t.desde
          ? 'en almuerzo'
          : t.almuerzo_largo
          ? `lleva ${lapso(t.desde)} de almuerzo`
          : `almuerza desde las ${horaCorta(t.desde)}`,
        alerta: t.almuerzo_largo,
      }
    case 'en_obra':
      return { linea: t.desde ? `desde las ${horaCorta(t.desde)}` : 'en la obra', alerta: false }
    case 'por_llegar': {
      if (!t.desde) return { linea: 'tiene trabajo hoy', alerta: false }
      /*
       * Tres casos distintos que antes salían con la misma frase («obra desde
       * el 30 de sept de 2026, hoy no ha llegado»):
       *   · la orden ya terminó su horario y nunca llegó: eso es un NO LLEGÓ;
       *   · es de varios días, empezó antes y hoy no ha marcado;
       *   · es de hoy: a qué hora llega o debía llegar.
       */
      if (t.hasta && new Date(t.hasta).getTime() < Date.now()) {
        const ini = dia(t.desde)
        const fin = dia(t.hasta)
        return {
          linea: `no llegó · era ${ini === fin ? `el ${ini}` : `del ${ini} al ${fin}`}`,
          alerta: true,
        }
      }
      if (!esDeHoy(t.desde))
        return { linea: `hoy no ha llegado · obra desde el ${dia(t.desde)}`, alerta: true }
      const tarde = new Date(t.desde).getTime() < Date.now()
      return {
        linea: `${tarde ? 'debía llegar a las' : 'llega a las'} ${horaCorta(t.desde)}`,
        alerta: tarde,
      }
    }
    default:
      return { linea: '', alerta: false }
  }
}

export function DondeEstaElEquipo() {
  const navegar = useNavigate()
  const { tecnicos, cargando, error } = useEstadoDelEquipo()

  const total = tecnicos.length
  const conTrabajo = tecnicos.filter((t) => t.estado !== 'sin_trabajo')
  // Los chips, agrupados por estado (los colores juntos) y, dentro, por nombre.
  // Entra también quien no tiene trabajo hoy PERO tiene herramienta afuera:
  // esa herramienta debería estar en bodega.
  const enChips = tecnicos
    .filter((t) => t.estado !== 'sin_trabajo' || t.herramientas > 0)
    .sort(
      (a, b) =>
        ORDEN_CHIP.indexOf(a.estado) - ORDEN_CHIP.indexOf(b.estado) ||
        a.nombre.localeCompare(b.nombre),
    )
  const herramientasAfuera = tecnicos.reduce((s, t) => s + (t.herramientas ?? 0), 0)
  const sinTrabajo = tecnicos.filter((t) => t.estado === 'sin_trabajo' && !t.herramientas).length
  const cuantos = (e: Estado) => tecnicos.filter((t) => t.estado === e).length
  const alertas = conTrabajo.map((t) => ({ t, d: detalle(t) })).filter(({ d }) => d.alerta)

  const abrir = (t: EstadoDelTecnico) => {
    if (t.orden_servicio_id) navegar(`/ordenes/${t.orden_servicio_id}`)
  }

  return (
    <section className={estilos.panel} aria-label="Dónde está el equipo">
      <div className={estilos.cuerpo}>
        {/* ── La izquierda: el número y lo que pide atención ─────────────── */}
        <div className={estilos.izquierda}>
          <p className={estilos.rotulo}>
            <span className={estilos.insignia}>
              <Icono estado="equipo" />
            </span>
            <span>
              Dónde está el equipo
              <small>ahora · se actualiza solo</small>
            </span>
          </p>

          <p className={estilos.cifraGrande}>
            {cargando ? '…' : conTrabajo.length}
            {!cargando && <span className={estilos.deCuantos}> de {total}</span>}
          </p>
          <p className={estilos.estadoGeneral}>
            <i className={alertas.length ? estilos.puntoAlerta : estilos.puntoBien} />
            {error
              ? error
              : alertas.length === 0
              ? 'con trabajo en curso · todo en orden'
              : `con trabajo en curso · ${alertas.length} ${
                  alertas.length === 1 ? 'pide' : 'piden'
                } atención`}
          </p>
          {herramientasAfuera > 0 && (
            <p className={estilos.herramientasAfuera}>
              <Llave />
              {herramientasAfuera} {herramientasAfuera === 1 ? 'herramienta' : 'herramientas'}{' '}
              afuera
            </p>
          )}
        </div>

        {/* ── La derecha: los cinco estados ───────────────────────────────── */}
        <div className={estilos.estados}>
          {ESTADOS.map((e) => {
            const n = cuantos(e.estado)
            return (
              <div key={e.estado} className={`${estilos.estado} ${estilos[e.tono]}`}>
                <p className={estilos.estadoTope}>
                  <span className={estilos.estadoIcono}>
                    <Icono estado={e.estado} />
                  </span>
                  {e.titulo}
                </p>
                <p className={`${estilos.estadoCifra} ${n === 0 ? estilos.cero : ''}`}>{n}</p>
                <p className={estilos.estadoPie}>{e.pie}</p>
                <span className={estilos.barra}>
                  {/* Sobre los que tienen trabajo hoy: con once sin trabajo, sobre
                      el total la barra no se veía. */}
                  <span
                    style={{ width: `${conTrabajo.length ? (n / conTrabajo.length) * 100 : 0}%` }}
                  />
                </span>
              </div>
            )
          })}
        </div>
      </div>

      {/* ── Cada uno, con lo que está haciendo ─────────────────────────────
          Johan: «¿no es mejor que en vez de "Quién" todos salgan con la
          descripción, así como esos cuatro? Pero rediseñado, que se vea
          estético». Una ficha por técnico con el mismo formato de las
          alertas: su estado en un círculo de color, el nombre, qué hace, la
          orden y su herramienta. Ordenadas por urgencia; las que piden
          atención llevan el fondo y el borde de su color, el resto apenas un
          velo, para que el ojo vaya primero a esas. */}
      <div className={estilos.personas}>
        {enChips.map((t) => {
          const d = detalle(t)
          return (
            <button
              key={t.colaborador_id}
              type="button"
              className={`${estilos.persona} ${estilos[TONO[t.estado]]} ${
                d.alerta || (t.estado === 'sin_trabajo' && t.herramientas > 0)
                  ? estilos.personaAlerta
                  : ''
              }`}
              onClick={() => abrir(t)}
              disabled={!t.orden_servicio_id}
              title={
                t.herramientas_completo
                  ? `Herramienta afuera:
${t.herramientas_completo}`
                  : undefined
              }
            >
              <span className={estilos.personaIcono}>
                <Icono estado={t.estado === 'sin_trabajo' ? 'equipo' : t.estado} />
              </span>
              <span className={estilos.personaTexto}>
                <span className={estilos.personaNombre}>
                  {t.nombre.split(' ').slice(0, 3).join(' ')}
                </span>
                <span className={estilos.personaEstado}>
                  {/* 0295: si ya terminó hoy, se dice eso, no «sin trabajo». */}
                  {t.estado !== 'sin_trabajo'
                    ? d.linea
                    : t.orden_numero
                    ? `terminó ${t.orden_numero}${
                        t.hasta ? ` a las ${horaCorta(t.hasta)}` : ''
                      } · falta devolver la herramienta`
                    : 'sin trabajo hoy, con herramienta afuera'}
                </span>
                {/* LA HERRAMIENTA QUE TIENE, AHÍ MISMO (Johan). Corta: la
                    primera palabra y la etiqueta, que es lo que se busca en
                    bodega. «No tiene que ser la descripción completa»: esa
                    queda al pasar el mouse. */}
                {t.herramientas_detalle && (
                  <span className={estilos.personaHerramienta}>
                    <Llave />
                    <span>{t.herramientas_detalle}</span>
                  </span>
                )}
                {(t.orden_numero || t.cliente) && (
                  <span className={estilos.personaObra}>
                    {t.orden_numero && <b>{t.orden_numero}</b>}
                    {t.cliente && <> · {t.cliente}</>}
                  </span>
                )}
              </span>
              {t.orden_servicio_id && <span className={estilos.abrir}>Abrir ›</span>}
            </button>
          )
        })}
      </div>

      {sinTrabajo > 0 && (
        <p className={estilos.sinTrabajoLinea}>
          <b>Sin trabajo hoy · {sinTrabajo}</b>{' '}
          {tecnicos
            .filter((t) => t.estado === 'sin_trabajo' && !t.herramientas)
            .map((t) => t.nombre.split(' ').slice(0, 2).join(' '))
            .join(' · ')}
        </p>
      )}
    </section>
  )
}
